import { DocumentTransform } from '@apollo/client';
import { Kind, parse } from 'graphql';
import type { DocumentNode, FragmentDefinitionNode } from 'graphql';

import Cookie from 'js/lib/cookie';
import { isCourserian } from 'js/lib/user';

import { getRecommendationCollectionMetadataFragment } from 'bundles/recommendations-common/lib/recommendationCollectionMetadata';
import { RECOMMENDATIONS_EXPLAINER_COOKIE } from 'bundles/recommendations-common/lib/recommendationsExplainerFlag';
import RecommendationExplainerDocument from 'bundles/recommendations-common/queries/RecommendationExplainer.graphql';

export { RECOMMENDATIONS_EXPLAINER_COOKIE } from 'bundles/recommendations-common/lib/recommendationsExplainerFlag';
export { getRecommendationCollectionMetadataFragment } from 'bundles/recommendations-common/lib/recommendationCollectionMetadata';

const recommendationCollectionDebugFields = parse(`
  fragment RecommendationCollectionDebugFields on Recommendation_Collection {
    explainer {
      ...RecommendationExplainer
    }
    programs {
      id
    }
  }
`).definitions[0] as FragmentDefinitionNode;

const recommendationExplainerFragments = RecommendationExplainerDocument.definitions.filter(
  (definition): definition is FragmentDefinitionNode => definition.kind === Kind.FRAGMENT_DEFINITION
);

/**
 * Keeps learner documents, bundles and test mocks as on main because `explainer` is `@Secured(COURSERIAN)`.
 *
 * `RecommendationCollectionMetadata` is the marker. This adds `explainer { ...RecommendationExplainer }` and
 * `programs { id }`, including the required explainer fragment definitions.
 *
 * Apollo installs the transform only when `isRecsDebugRequested()` is true and applies it to requests and cache
 * writes. Direct `client.readQuery` calls bypass this custom transform.
 *
 * SSR installs the same transform to avoid a hydration refetch. Applying the transform more than once is a no-op.
 *
 * See bundles/recommendations-explainer/README.md
 */
const transformRecsExplainerDocument = (document: DocumentNode) => {
  const metadataFragment = getRecommendationCollectionMetadataFragment(document);

  if (!metadataFragment) {
    return document;
  }

  const existingFields = new Set(
    metadataFragment.selectionSet.selections
      .filter((selection) => selection.kind === Kind.FIELD)
      .map((selection) => selection.name.value)
  );
  const addedDefinitions = recommendationExplainerFragments.filter(
    (fragment) =>
      !document.definitions.some(
        (definition) => definition.kind === Kind.FRAGMENT_DEFINITION && definition.name.value === fragment.name.value
      )
  );

  return {
    ...document,
    definitions: [
      ...document.definitions.map((definition) =>
        definition === metadataFragment
          ? {
              ...metadataFragment,
              selectionSet: {
                ...metadataFragment.selectionSet,
                selections: [
                  ...metadataFragment.selectionSet.selections,
                  ...recommendationCollectionDebugFields.selectionSet.selections.filter(
                    (selection) => selection.kind !== Kind.FIELD || !existingFields.has(selection.name.value)
                  ),
                ],
              },
            }
          : definition
      ),
      ...addedDefinitions,
    ],
  };
};

let recsExplainerDocumentTransform: DocumentTransform | undefined;

export const getRecsExplainerDocumentTransform = () => {
  recsExplainerDocumentTransform ??= new DocumentTransform(transformRecsExplainerDocument);
  return recsExplainerDocumentTransform;
};

let recsDebugActive = false;

export function isRecsDebugRequested(): boolean {
  return Cookie.get(RECOMMENDATIONS_EXPLAINER_COOKIE) === '1' && isCourserian();
}

export function isRecsDebugActive(): boolean {
  return recsDebugActive;
}

export function setRecsDebugActive(enabled: boolean): void {
  recsDebugActive = enabled;
}
